/** Global SSE subscription — feeds zustand store directly, no TanStack refetch cascade */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDashboardStore, RECENT_CHUNKS_MAX, type StreamStats, type ChunkFeedEvent } from "./store";

/**
 * Chunk events must be coalesced, not applied per-message: during DVR backfill
 * or cleanup passes the server emits dozens of events per second, and a store
 * write per event re-renders the whole app (tree stats subscribe to the full
 * stats object). When events arrive faster than the main thread can render,
 * the tab's task queue and heap grow until Chrome kills it. Buffer the latest
 * stats per stream and flush on a fixed tick instead.
 */
const FLUSH_MS = 500;

export function useSSE() {
    const hydrate = useDashboardStore(s => s.hydrate);
    const updateStatsBatch = useDashboardStore(s => s.updateStatsBatch);
    const setConnected = useDashboardStore(s => s.setConnected);
    const qc = useQueryClient();
    const esRef = useRef<EventSource | null>(null);

    useEffect(() => {
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        const pendingStats = new Map<string, StreamStats>();
        let pendingChunks: ChunkFeedEvent[] = [];
        let pendingVersion = 0;

        const flushTimer = setInterval(() => {
            if (pendingStats.size === 0 && pendingChunks.length === 0) return;
            updateStatsBatch([...pendingStats.entries()], pendingChunks, pendingVersion);
            pendingStats.clear();
            pendingChunks = [];
        }, FLUSH_MS);

        function connect() {
            if (esRef.current) esRef.current.close();
            const es = new EventSource("/api/feed");
            esRef.current = es;

            es.onmessage = (e) => {
                const event = JSON.parse(e.data);

                if (event.type === "init") {
                    // Full snapshot — anything buffered from before is stale
                    pendingStats.clear();
                    pendingChunks = [];
                    hydrate(event.streams, event.version);
                    return;
                }

                if (event.type === "chunk") {
                    pendingStats.set(event.streamKey, event.stats);
                    pendingChunks.push({ streamKey: event.streamKey, sequence: event.sequence, size: event.size, ts: event.ts });
                    if (pendingChunks.length > RECENT_CHUNKS_MAX) {
                        pendingChunks = pendingChunks.slice(-RECENT_CHUNKS_MAX);
                    }
                    pendingVersion = event.version;
                    return;
                }

                if (event.type === "live:block" || event.type === "live:caption") {
                    // New announcement detected / transcription arrived — refresh that stream's list
                    qc.invalidateQueries({ queryKey: ["liveBlocks", event.streamKey] });
                    return;
                }

                if (event.type === "stream:started" || event.type === "stream:ended") {
                    qc.invalidateQueries({ queryKey: ["streams"] });
                    return;
                }

                if (event.type === "stream:discovered") {
                    qc.invalidateQueries({ queryKey: ["streams"] });
                    qc.invalidateQueries({ queryKey: ["events"] });
                }
            };

            es.onerror = () => {
                setConnected(false);
                es.close();
                esRef.current = null;
                reconnectTimer = setTimeout(connect, 3000);
            };
        }

        connect();
        return () => {
            clearInterval(flushTimer);
            if (reconnectTimer) clearTimeout(reconnectTimer);
            esRef.current?.close();
        };
    }, [hydrate, updateStatsBatch, setConnected, qc]);
}
